// Kanemori Sanctuary (M10): the ground, slope edges, upright props and lights of the sanctuary strip. Called from
// hd-scene.ts like the other places: paintSanctuary on the ground before the baked lights, sanctuaryProps with the
// props. The road and courtyards are repainted over the generic alley/plaza floor; the forest and temple roofs are in
// hd-sanctuary.ts. Restored half (checkpoint, road, sanctuary): crisp, lantern-lit. Monastery (north of the ridge
// wall): mossy, broken, unlit, under a baked mist. Presentation only: no collision or gameplay point moves.
import { WORLD, type HdScene, type Rect } from '../../common/src/data';
import { T, TILE, tileAt, solidFoot, type GameMap, inTileRect } from '../../common/src/map';
import { hash2 } from '../../common/src/util';
import { canvas } from './canvas';
import { HT, K, type G, px, speckle, dither, disc, frame, dk, lt, mix, alpha, shadow, text, textW } from './hd-kit';
import type { LightFn } from './hd-registry';
import type { HdLight } from './hd-scene';
import { SK, NEW_TILE } from './hd-sanctuary';
import { paintCountry, countryProps } from './hd-sanctuary-country';
import { featureAt } from '../../common/src/country';

type Prop = (cv: HTMLCanvasElement, wx: number, wy: number, ax: number, ay: number) => void;
interface Sprite { cv: HTMLCanvasElement; ax: number; ay: number }

/** first tile column of the strip */
export const SANCT_X = 256;
/** rows north of this are the monastery (dim, mossy); south of it the restored sanctuary */
const RIDGE_Y = 44;
export const inSanctuary = (tx: number) => tx >= SANCT_X;
const isMona = (ty: number) => ty < RIDGE_Y;
const ROAD_NAMES = new Set(['Sanctuary approach', 'Mountain road', 'Ridge road']);
/** the boundary wall and its gate piers (hd-sanctuary-wall.ts) draw their own edges: no retaining wall or rail over them */
const BOUNDARY_ROOFS = new Set(['sanctWall', 'sanctWallOld', 'sanctGateRoof']);
const walk = (k: T) => k === T.ROAD || k === T.SIDEWALK || k === T.ALLEY || k === T.PLAZA;
const plaza = (name: string) => WORLD.plazas.find(p => p.name === name);

// ================================================================== ground
export function paintSanctuary(g: G, m: GameMap, S: HdScene, R: Rect, L: LightFn) {
  if (R.x + R.w <= SANCT_X - 6) return;
  const t = (x: number, y: number) => tileAt(m, x, y);
  const X = (tx: number) => (tx - R.x) * HT, Y = (ty: number) => (ty - R.y) * HT;
  const fill = new Set<number>(); // tiles of the forest filler (their edges get retaining walls and rails)
  for (const b of S.buildings) if (b.kind === 'sanctFill' && !BOUNDARY_ROOFS.has(b.roof)) for (let y = b.y; y < b.y + b.h; y++) for (let x = b.x; x < b.x + b.w; x++) fill.add(y * m.w + x);
  const isFill = (x: number, y: number) => fill.has(y * m.w + x);

  // ---- the mountain road: fresh asphalt, white edge lines, an amber centre line on the three-tile road
  for (const a of WORLD.alleys) if (ROAD_NAMES.has(a.name)) for (let ty = a.y; ty < a.y + a.h; ty++) for (let tx = a.x; tx < a.x + a.w; tx++) {
    if (!inTileRect(tx, ty, R) || t(tx, ty) !== T.ALLEY) continue;
    road(g, X(tx), Y(ty), tx, ty, t);
  }
  gateSill(g, X, Y, R);
  railEnd(g, m, X, Y, R, L);
  // ---- courtyards
  for (let ty = R.y; ty < R.y + R.h; ty++) for (let tx = Math.max(R.x, SANCT_X); tx < R.x + R.w; tx++) {
    const k = t(tx, ty); if (k !== T.PLAZA && k !== T.LOW && k !== T.THIN || (k === T.LOW ? featureAt(tx, ty) : featureAt(tx, ty, 'reststop'))) continue; // countryside features: hd-sanctuary-country.ts
    const x = X(tx), y = Y(ty);
    if (inTileRect(tx, ty, plaza('Sanctuary checkpoint')!) || inTileRect(tx, ty, plaza('Sanctuary checkpoint south')!)) corpPad(g, x, y, tx, ty);
    else if (inTileRect(tx, ty, plaza('Monastery cemetery')!)) mossGround(g, x, y, tx, ty, 0.35);
    else if (inTileRect(tx, ty, plaza('Monastery moss court')!)) mossGround(g, x, y, tx, ty, 0.75);
    else if (isMona(ty)) brokenFlags(g, x, y, tx, ty);
    else gravel(g, x, y, tx, ty);
  }
  const court = plaza('Sanctuary courtyard'), wc = plaza('Sanctuary west court'), ec = plaza('Sanctuary east court');
  if (court && inTileRect(court.x, court.y, R)) {
    // flagstone paths: hall stair to the south wall, and across from court to court; the zen garden between its curbs
    flagPath(g, X(304), Y(court.y), 4 * HT, court.h * HT, false);
    flagPath(g, X(court.x), Y(77), court.w * HT, 2 * HT, true);
    if (wc) flagPath(g, X(wc.x + 2), Y(wc.y), 4 * HT, wc.h * HT, false);
    if (ec) flagPath(g, X(ec.x + 2), Y(ec.y), 4 * HT, (ec.h) * HT, false);
    zenGarden(g, X(298), Y(72), 16 * HT, 3 * HT);
  }
  paintCountry(g, m, R); // the countryside: winding road, forest floor, garden, onsen, paddies (hd-sanctuary-country.ts)
  const mc = plaza('Monastery courtyard');
  if (mc && inTileRect(mc.x, mc.y, R)) {
    flagPath(g, X(301), Y(mc.y), 4 * HT, mc.h * HT, false, true);
    // where Shingan sits: a worn meditation dais of old stone, its ring of candles long out
    const dx = X(306), dy = Y(31); px(g, dx - 2, dy - 2, 4 * HT + 4, 3 * HT + 4, K.ink); px(g, dx, dy, 4 * HT, 3 * HT, SK.stoneLo); frame(g, dx + 4, dy + 4, 4 * HT - 8, 3 * HT - 8, SK.stone, 2);
    speckle(g, dx, dy, 4 * HT, 3 * HT, SK.moss, 0.12, 31, 2); disc(g, dx + 2 * HT, dy + 1.5 * HT, 22, 16, alpha(K.night, 0.4));
  }

  // ---- low walls and posts that block movement (WORLD.lows / thins in the strip)
  for (const l of WORLD.lows) if (inSanctuary(l.x) && inTileRect(l.x, l.y, R) && !(featureAt(l.x, l.y) && (l.w > 1 || l.h > 1))) { // pond and pool water: hd-sanctuary-country.ts
    const x = X(l.x), y = Y(l.y), w = l.w * HT, h = l.h * HT;
    if (isMona(l.y) && l.w === 1) graveBase(g, x, y);
    else if (isMona(l.y)) pagodaRubble(g, x, y, w, h);
    else if (l.w === 1) lanternBase(g, x, y);
    else if (l.w === 2 && l.h === 2) corpBlock(g, x, y, L);
    else gardenCurb(g, x, y, w, h);
  }
  for (const r of WORLD.thins) if (inSanctuary(r.x) && inTileRect(r.x, r.y, R)) {
    const x = X(r.x), y = Y(r.y);
    if (isMona(r.y)) { disc(g, x + 16, y + 20, 12, 7, alpha(K.night, 0.4)); px(g, x + 6, y + 10, 20, 14, K.ink); px(g, x + 7, y + 11, 18, 12, SK.stoneLo); speckle(g, x + 7, y + 11, 18, 12, SK.moss, 0.35, r.x); }
    else { // a steel bollard with the guard post's card reader
      shadow(g, x + 12, y + 22, 14, 6); px(g, x + 10, y + 4, 12, 22, K.ink); px(g, x + 11, y + 5, 10, 20, K.metal); px(g, x + 11, y + 5, 3, 20, K.metalHi);
      px(g, x + 11, y + 9, 10, 3, K.warm); px(g, x + 13, y + 15, 6, 3, K.cyan); L(x + 8, y + 10, 16, 10, K.cyan, 0.3);
    }
  }
  // concrete pads under the hackable transformers so they read as kit, not scenery
  for (const f of WORLD.fixtures) if (inSanctuary(f.x) && inTileRect(Math.floor(f.x), Math.floor(f.y), R)) {
    const x = X(f.x) - 22, y = Y(f.y) - 18; px(g, x - 2, y - 2, 48, 40, K.ink); px(g, x, y, 44, 36, K.concrete); frame(g, x + 2, y + 2, 40, 32, K.warm, 2);
    for (let i = 4; i < 40; i += 8) px(g, x + i, y + 32, 4, 2, K.ink);
  }

  // ---- slope edges: retaining walls where the forest rises above a path, guard rails where the road runs along a drop
  for (let ty = R.y; ty < R.y + R.h; ty++) for (let tx = Math.max(R.x, SANCT_X); tx < R.x + R.w; tx++) {
    if (!isFill(tx, ty)) continue;
    const x = X(tx), y = Y(ty), mona = isMona(ty);
    const s = t(tx, ty + 1), n = t(tx, ty - 1), w = t(tx - 1, ty), e = t(tx + 1, ty);
    if (walk(s)) ishigaki(g, x, y + HT - 18, tx, ty, mona);
    if (walk(n)) n === T.ALLEY && !mona ? railH(g, x, y) : curbH(g, x, y, mona);
    if (walk(w) && !walk(s)) w === T.ALLEY && !mona ? railV(g, x, y) : curbV(g, x, y, mona);
    if (walk(e) && !walk(s)) e === T.ALLEY && !mona ? railV(g, x + HT - 8, y) : curbV(g, x + HT - 6, y, mona);
  }

  // ---- monastery mist, baked into the ground (still reads with the lighting off; the comfort grade dims it with the rest)
  const my1 = Math.min(R.y + R.h, RIDGE_Y + 2);
  if (R.y < my1) {
    const x0 = X(Math.max(R.x, SANCT_X + 20)), x1 = X(R.x + R.w);
    // soft banks of mist drifting across: wide low wisps in 4 px steps, thicker in slow bands, thinning at the ridge
    for (let ty = R.y; ty < my1; ty++) for (let j = 0; j < HT; j += 4) {
      const wy = ty * HT + j, yy = Y(ty) + j, f = ty >= RIDGE_Y ? 0.35 : 1;
      const band = 0.5 + 0.5 * Math.sin(wy / 53) * Math.cos(wy / 131);
      px(g, x0, yy, x1 - x0, 4, alpha(SK.mist, (0.02 + 0.045 * band) * f));
      for (let x = x0 - 96; x < x1; x += 96) {
        const cell = Math.floor((x - x0 + 96) / 96), v = hash2(cell, Math.floor(wy / 24));
        if (v < 0.55) continue;
        const ww = 80 + Math.floor(v * 90); px(g, Math.max(x0, x + Math.floor(hash2(wy >> 4, cell) * 40)), yy, Math.min(ww, x1 - x), 4, alpha(SK.mist, 0.05 * band * f));
      }
    }
  }
}

/** the granite sill laid across the road at the boundary wall's gate (x256-257, y84-86): dressed setts in place of asphalt, the road's edge lines and amber dashes running on over them */
function gateSill(g: G, X: (tx: number) => number, Y: (ty: number) => number, R: Rect) {
  for (let ty = 84; ty <= 86; ty++) for (let tx = 256; tx <= 257; tx++) {
    if (!inTileRect(tx, ty, R)) continue;
    const x = X(tx), y = Y(ty);
    px(g, x, y, HT, HT, K.ink);
    for (let j = 0; j < HT; j += 8) for (let i = (j / 8 % 2) * -8; i < HT; i += 16) {
      const v = hash2(tx * 16 + i, ty * 32 + j), c = v < 0.25 ? SK.stoneHi : v < 0.7 ? SK.stone : SK.stoneLo, a0 = Math.max(0, i), w = Math.min(15, HT - a0 - 1);
      px(g, x + a0 + 1, y + j + 1, w + (i < 0 ? i : 0), 6, c); px(g, x + a0 + 1, y + j + 1, w + (i < 0 ? i : 0), 1, lt(c, 0.15));
    }
    if (ty === 84) px(g, x, y + 3, HT, 2, K.paint);
    if (ty === 86) px(g, x, y + HT - 5, HT, 2, K.paint);
    if (ty === 85) for (let i = 0; i < HT; i++) if (((x + i) % 24) < 12) px(g, x + i, y + HT / 2 - 1, 1, 2, K.lane);
  }
}
/** where the station line ends against the wall (Nishi-Shinjuku, x255): a concrete buffer stop with red and white striped
 *  end plates and two sprung buffers on each track, a red lamp on top, so the rails don't just run into the stone */
function railEnd(g: G, m: GameMap, X: (tx: number) => number, Y: (ty: number) => number, R: Rect, L: LightFn) {
  const r = WORLD.rails.find(q => q.x + q.w === SANCT_X); if (!r || !inTileRect(SANCT_X - 1, r.y, R) || tileAt(m, SANCT_X - 1, r.y) !== T.RAIL) return;
  const top = Y(r.y), h = r.h * HT, x0 = X(SANCT_X - 1) + 4;
  for (const c of [Math.round(h * 0.3), Math.round(h * 0.7)]) {
    const y0 = top + c - 20, bh = 36;
    px(g, x0 - 2, y0 + 3, 28, bh, alpha(K.night, 0.45)); // cast shadow
    px(g, x0 + 4, y0, 22, bh, K.ink); px(g, x0 + 5, y0 + 1, 20, bh - 2, K.concrete); px(g, x0 + 5, y0 + 1, 20, 2, K.concreteHi); px(g, x0 + 22, y0 + 1, 3, bh - 2, K.concreteLo);
    for (let j = 0; j < bh - 2; j += 6) { px(g, x0, y0 + 1 + j, 6, 6, K.ink); px(g, x0 + 1, y0 + 2 + j, 4, 4, (j / 6) % 2 ? K.white : K.red); } // striped end plate
    for (const o of [-8, 12]) { px(g, x0 - 3, y0 + 18 + o - 4, 8, 8, K.ink); px(g, x0 - 2, y0 + 18 + o - 3, 6, 6, K.metal); px(g, x0 - 2, y0 + 18 + o - 3, 6, 2, K.metalHi); px(g, x0 + 4, y0 + 18 + o - 3, 3, 6, K.metalLo); } // the two buffers
    px(g, x0 + 14, y0 + 12, 6, 10, K.ink); px(g, x0 + 15, y0 + 13, 4, 8, K.red); L(x0 + 10, y0 + 8, 14, 14, K.red, 0.45, false, true);
  }
}
function road(g: G, x: number, y: number, tx: number, ty: number, t: (x: number, y: number) => T) {
  px(g, x, y, HT, HT, K.asphalt); speckle(g, x, y, HT, HT, K.asphaltHi, 0.07, 1); speckle(g, x, y, HT, HT, K.asphaltLo, 0.08, 2);
  if (hash2(tx, ty * 3) < 0.12) { px(g, x + 6, y + 8, 18, 10, K.asphaltHi); speckle(g, x + 6, y + 8, 18, 10, K.asphalt, 0.3, 13); } // patches
  const A = (dx: number, dy: number) => t(tx + dx, ty + dy) === T.ALLEY || t(tx + dx, ty + dy) === T.PLAZA;
  if (!A(0, -1)) px(g, x, y + 3, HT, 2, K.paint);
  if (!A(0, 1)) px(g, x, y + HT - 5, HT, 2, K.paint);
  if (!A(-1, 0)) px(g, x + 3, y, 2, HT, K.paint);
  if (!A(1, 0)) px(g, x + HT - 5, y, 2, HT, K.paint);
  const vert = A(0, -1) && A(0, 1) && A(-1, 0) && A(1, 0) && !A(-2, 0) && !A(2, 0);
  const horiz = A(-1, 0) && A(1, 0) && A(0, -1) && A(0, 1) && !A(0, -2) && !A(0, 2);
  if (vert) for (let j = 0; j < HT; j++) if (((y + j) % 24) < 12) px(g, x + HT / 2 - 1, y + j, 2, 1, K.lane);
  if (horiz) for (let i = 0; i < HT; i++) if (((x + i) % 24) < 12) px(g, x + i, y + HT / 2 - 1, 1, 2, K.lane);
}
/** raked white gravel of the restored courtyards */
function gravel(g: G, x: number, y: number, tx: number, ty: number) {
  const base = mix(SK.stoneHi, K.white, 0.25);
  px(g, x, y, HT, HT, dk(base, 0.18)); speckle(g, x, y, HT, HT, base, 0.16, 61); speckle(g, x, y, HT, HT, dk(base, 0.42), 0.1, 62);
  for (let j = 4; j < HT; j += 6) px(g, x, y + j, HT, 1, alpha(K.night, 0.14));
  if (hash2(tx, ty) < 0.04) disc(g, x + 16, y + 18, 5, 3, SK.stoneLo);
}
function corpPad(g: G, x: number, y: number, tx: number, ty: number) {
  px(g, x, y, HT, HT, K.concrete); speckle(g, x, y, HT, HT, K.concreteLo, 0.06, 3);
  px(g, x, y, HT, 1, K.concreteLo); px(g, x, y, 1, HT, K.concreteLo);
  if (hash2(tx, ty) < 0.15) speckle(g, x + 4, y + 4, 20, 16, alpha(K.night, 0.3), 0.3, 9, 2);
}
/** moss over packed earth with old stones showing through; m = how much moss */
function mossGround(g: G, x: number, y: number, tx: number, ty: number, m: number) {
  px(g, x, y, HT, HT, m > 0.5 ? mix(SK.moss, SK.mossLo, 0.45) : mix(SK.mossLo, SK.stoneLo, 0.4));
  speckle(g, x, y, HT, HT, SK.moss, 0.12 * m + 0.03, 71, 4); speckle(g, x, y, HT, HT, SK.mossHi, 0.04 * m, 72, 2);
  speckle(g, x, y, HT, HT, SK.stone, 0.05 * (1 - m), 73, 4);
  if (m > 0.5 && hash2(tx * 3, ty) < 0.2) { const sx = x + 6 + Math.floor(hash2(tx, ty * 5) * 14); disc(g, sx + 6, y + 18, 8, 5, K.ink); disc(g, sx + 6, y + 17, 7, 4, SK.stoneLo); px(g, sx + 1, y + 14, 8, 1, SK.stone); } // stepping stone
  if (hash2(ty, tx * 7) < 0.15) for (let k = 0; k < 4; k++) px(g, x + 8 + k * 3, y + 10 - (k % 2) * 3, 1, 5, SK.mossHi); // fern
}
/** the monastery's broken flagstones: tilted slabs, moss in every joint, some missing */
function brokenFlags(g: G, x: number, y: number, tx: number, ty: number) {
  px(g, x, y, HT, HT, SK.mossLo); // moss in every joint
  for (let j = 0; j < HT; j += 16) for (let i = 0; i < HT; i += 16) {
    const v = hash2(x + i, y + j); if (v < 0.12) { px(g, x + i + 2, y + j + 2, 12, 12, SK.moss); px(g, x + i + 4, y + j + 3, 5, 3, SK.mossHi); continue; }
    const c = v < 0.45 ? SK.stoneLo : v < 0.85 ? mix(SK.stoneLo, SK.stone, 0.5) : SK.stone;
    px(g, x + i + 1, y + j + 1, 14, 14, c); px(g, x + i + 1, y + j + 1, 14, 1, lt(c, 0.15)); px(g, x + i + 1, y + j + 14, 14, 1, dk(c, 0.3));
    if (v > 0.7) px(g, x + i + 3, y + j + 6, 9, 1, SK.stoneDk); // crack
    if (v > 0.5 && v < 0.6) px(g, x + i + 9, y + j + 9, 5, 4, SK.moss);
  }
  if (hash2(tx, ty * 9) < 0.1) for (let k = 0; k < 5; k++) px(g, x + 10 + k * 3, y + 14 - (k % 2) * 4, 1, 6, SK.mossHi);
}
function flagPath(g: G, x: number, y: number, w: number, h: number, across: boolean, old = false) {
  const sw = across ? 30 : 32, sh = across ? 32 : 22;
  for (let j = 0; j + 4 < h; j += sh) for (let i = 0; i + 4 < w; i += sw) {
    const v = hash2(x + i, y + j); if (old && v < 0.2) continue;
    const c = old ? mix(SK.stoneLo, SK.moss, v * 0.4) : v < 0.5 ? SK.stone : mix(SK.stone, SK.stoneHi, 0.4);
    px(g, x + i + 2, y + j + 2, sw - 4, sh - 4, K.ink); px(g, x + i + 3, y + j + 3, sw - 6, sh - 6, c); px(g, x + i + 3, y + j + 3, sw - 6, 1, lt(c, 0.2));
    if (old) speckle(g, x + i + 2, y + j + 2, sw - 4, sh - 4, SK.moss, 0.12, i + j, 1);
  }
}
/** raked sand in long lines that ring three rocks, between the courtyard's garden curbs */
function zenGarden(g: G, x: number, y: number, w: number, h: number) {
  const sand = mix(K.white, SK.stoneHi, 0.35);
  px(g, x, y, w, h, sand);
  const rocks = [[x + 90, y + 40, 22, 14], [x + 250, y + 52, 16, 11], [x + 380, y + 36, 26, 16]];
  for (let yy = y + 3; yy < y + h; yy += 5) for (let xx = x; xx < x + w; xx++) {
    if (rocks.some(([cx, cy, rx, ry]) => ((xx - cx) / (rx + 18)) ** 2 + ((yy - cy) / (ry + 14)) ** 2 < 1)) continue;
    px(g, xx, yy, 1, 1, dk(sand, 0.22));
  }
  for (const [cx, cy, rx, ry] of rocks) {
    for (let k = 3; k >= 1; k--) { g.save(); g.strokeStyle = dk(sand, 0.22); g.lineWidth = 1; g.beginPath(); g.ellipse(cx, cy, rx + k * 6, ry + k * 5, 0, 0, Math.PI * 2); g.stroke(); g.restore(); }
    disc(g, cx + 3, cy + 4, rx, ry, alpha(K.night, 0.35)); disc(g, cx, cy, rx, ry, K.ink); disc(g, cx, cy, rx - 1, ry - 1, SK.stone);
    disc(g, cx - 3, cy - 3, rx - 6, ry - 5, mix(SK.stone, SK.stoneHi, 0.6)); px(g, cx - rx + 6, cy - ry + 4, rx, 2, SK.stoneHi); speckle(g, cx - rx, cy, rx * 2, ry, SK.moss, 0.15, cx, 2);
  }
}
function gardenCurb(g: G, x: number, y: number, w: number, h: number) {
  // a low clipped-azalea hedge on a cut-stone curb
  px(g, x, y + 8, w, h - 8, K.ink); px(g, x + 1, y + h - 10, w - 2, 8, SK.stone); px(g, x + 1, y + h - 10, w - 2, 1, SK.stoneHi);
  for (let i = 0; i < w; i += 32) px(g, x + i, y + h - 10, 1, 8, SK.stoneDk);
  px(g, x + 1, y + 6, w - 2, h - 16, SK.cedar); for (let i = 3; i < w - 3; i += 6) disc(g, x + i, y + 8, 4, 3, i % 12 ? SK.cedarHi : mix(SK.cedarHi, K.pink, 0.25));
  shadow(g, x + 3, y + h - 2, w - 2, 4, 0.35);
}
function lanternBase(g: G, x: number, y: number) { px(g, x + 2, y + 18, 28, 12, K.ink); px(g, x + 3, y + 19, 26, 10, SK.stoneLo); px(g, x + 3, y + 19, 26, 2, SK.stone); }
function graveBase(g: G, x: number, y: number) {
  px(g, x, y + 8, HT, HT - 8, K.ink); px(g, x + 1, y + 9, HT - 2, HT - 10, SK.stoneLo); speckle(g, x + 1, y + 9, HT - 2, HT - 10, SK.moss, 0.35, x, 2);
  px(g, x + 1, y + 9, HT - 2, 1, SK.stone);
}
/** the collapsed pagoda: a heap of tiles, stone and charred beams on its old platform */
function pagodaRubble(g: G, x: number, y: number, w: number, h: number) {
  px(g, x - 4, y - 4, w + 8, h + 8, K.ink); px(g, x - 2, y - 2, w + 4, h + 4, SK.stoneLo); frame(g, x, y, w, h, SK.stone, 2);
  for (let k = 0; k < 70; k++) {
    const a = hash2(k, 3), b = hash2(3, k), cx = x + 6 + Math.floor(a * (w - 12)), cy = y + 6 + Math.floor(b * (h - 12)), v = hash2(k, k);
    if (v < 0.35) { px(g, cx - 6, cy - 2, 14, 5, K.ink); px(g, cx - 5, cy - 1, 12, 3, mix(K.slate, SK.moss, 0.2)); px(g, cx - 5, cy - 1, 12, 1, lt(K.slate, 0.2)); }
    else if (v < 0.55) { px(g, cx - 2, cy - 12, 5, 26, K.ink); px(g, cx - 1, cy - 11, 3, 24, SK.ash); px(g, cx - 1, cy - 11, 1, 24, SK.ashHi); }
    else { disc(g, cx, cy, 5, 4, K.ink); disc(g, cx, cy, 4, 3, SK.stoneHi); }
  }
  speckle(g, x, y, w, h, SK.moss, 0.14, 17, 2);
  // the toppled finial lying across the heap
  for (let k = 0; k < 80; k++) px(g, x + 14 + k, y + h - 30 - Math.round(k * 0.35), 4, 4, k % 12 < 2 ? SK.verdigris : SK.bronze);
}
function corpBlock(g: G, x: number, y: number, L: LightFn) {
  // a Kanemori planter block at the guard post: concrete, a clipped pine, the corp's cyan trim
  shadow(g, x + 6, y + 56, 60, 8); px(g, x + 2, y + 10, 60, 50, K.ink); px(g, x + 3, y + 11, 58, 12, K.concreteHi); px(g, x + 3, y + 23, 58, 35, K.concrete);
  px(g, x + 3, y + 23, 58, 2, K.cyan); L(x + 3, y + 22, 58, 4, K.cyan, 0.3);
  px(g, x + 6, y + 13, 52, 8, SK.cedarLo); for (let i = 10; i < 56; i += 7) disc(g, x + i, y + 13, 5, 4, i % 14 ? SK.cedar : SK.cedarHi);
  const s = 'K', tw = textW(s, 3); text(g, s, x + 32 - tw / 2, y + 32, lt(K.cyan, 0.4), 3);
}
/** ishigaki: the fitted stone retaining wall where the slope rises above a path */
function ishigaki(g: G, x: number, y: number, tx: number, ty: number, old: boolean) {
  px(g, x, y, HT, 18, K.ink);
  for (let j = 0; j < 18; j += 6) for (let i = ((j / 6) % 2) * -6; i < HT; i += 12) {
    const v = hash2(tx * 32 + i, ty * 18 + j), c = old ? mix(SK.stoneLo, SK.mossLo, v * 0.5) : v < 0.3 ? SK.stoneHi : v < 0.8 ? SK.stone : SK.stoneLo;
    px(g, x + Math.max(0, i) + 1, y + j + 1, Math.min(11, HT - Math.max(0, i) - 1), 5, c); px(g, x + Math.max(0, i) + 1, y + j + 1, Math.min(11, HT - Math.max(0, i) - 1), 1, lt(c, 0.15));
  }
  if (old) { speckle(g, x, y, HT, 18, SK.moss, 0.22, tx + ty, 2); if (hash2(tx, ty) < 0.25) px(g, x + 8, y + 4, 10, 10, K.ink); }
  px(g, x, y + 18, HT, 3, alpha(K.night, 0.45));
}
/** a steel guard rail seen from above along a drop below the road */
function railH(g: G, x: number, y: number) {
  px(g, x, y, HT, 10, alpha(K.night, 0.3));
  px(g, x, y + 2, HT, 5, K.ink); px(g, x, y + 3, HT, 3, K.metalHi); px(g, x, y + 5, HT, 1, K.metal);
  for (let i = 4; i < HT; i += 16) { px(g, x + i, y + 1, 4, 9, K.ink); px(g, x + i + 1, y + 2, 2, 6, K.metal); }
  if (((x / HT) | 0) % 3 === 0) { px(g, x + 12, y + 3, 6, 3, K.warm); } // reflector
}
function railV(g: G, x: number, y: number) {
  px(g, x, y, 8, HT, K.ink); px(g, x + 1, y, 5, HT, K.metalHi); px(g, x + 5, y, 1, HT, K.metal);
  for (let j = 4; j < HT; j += 16) { px(g, x, y + j, 8, 4, K.ink); px(g, x + 2, y + j + 1, 4, 2, K.metal); }
}
function curbH(g: G, x: number, y: number, old: boolean) { px(g, x, y, HT, 6, K.ink); px(g, x, y + 1, HT, 4, old ? SK.stoneLo : SK.stone); px(g, x, y + 1, HT, 1, SK.stoneHi); if (old) speckle(g, x, y, HT, 6, SK.moss, 0.3, x, 1); }
function curbV(g: G, x: number, y: number, old: boolean) { px(g, x, y, 6, HT, K.ink); px(g, x + 1, y, 4, HT, old ? SK.stoneLo : SK.stone); px(g, x + 1, y, 1, HT, SK.stoneHi); if (old) speckle(g, x, y, 6, HT, SK.moss, 0.3, y, 1); }

// ================================================================== upright sprites (cached)
const cache = new Map<string, Sprite>();
const memo = (k: string, f: () => Sprite) => cache.get(k) ?? (cache.set(k, f()), cache.get(k)!);

/** a Japanese cedar: straight red-brown trunk, a narrow cone of drooping needle tiers */
function cedar(v: number, old: boolean): Sprite {
  return memo(`cedar${v}${old}`, () => {
    const W = 52, H = 150, [cv, g] = canvas(W, H), ax = 26, ay = 146;
    shadow(g, 18, 142, 30, 6, 0.4);
    px(g, 22, 70, 8, 76, K.ink); px(g, 23, 70, 6, 76, K.woodLo); px(g, 23, 70, 2, 76, K.wood); // trunk
    for (let j = 76; j < 144; j += 7) px(g, 26, j, 2, 3, dk(K.woodLo, 0.3));
    if (old) speckle(g, 22, 100, 8, 44, SK.moss, 0.35, 5);
    const tiers = 7, c0 = old ? mix(SK.cedarLo, SK.mossLo, 0.3) : SK.cedarLo, c1 = old ? mix(SK.cedar, SK.moss, 0.3) : SK.cedar, c2 = old ? SK.mossHi : SK.cedarHi;
    for (let k = tiers - 1; k >= 0; k--) {
      const cy = 10 + k * 15, hw = 6 + Math.round(k * 2.9 + v * 3);
      for (let j = 0; j < 18; j++) { const ww = Math.round(hw * (0.35 + j / 26)); px(g, 26 - ww - 1, cy + j, ww * 2 + 2, 1, K.ink); px(g, 26 - ww, cy + j, ww * 2, 1, j < 4 ? c1 : c0); }
      for (let j = 2; j < 16; j += 3) { const ww = Math.round(hw * (0.35 + j / 26)); px(g, 26 - ww + 1, cy + j, Math.max(2, Math.round(ww * 0.7)), 1, c1); px(g, 26 - ww + 2, cy + j, 3, 1, c2); }
      for (let i = -hw; i <= hw; i += 4) px(g, 26 + i, cy + 17 + ((i + k) & 1), 2, 2, c0); // drooping tips
    }
    px(g, 25, 2, 2, 10, K.ink); px(g, 25, 4, 1, 8, c2);
    if (old) speckle(g, 6, 20, 40, 100, alpha(SK.mossHi, 0.6), 0.03, 9);
    return { cv, ax, ay };
  });
}
/** a stone lantern (ishi-doro) on the road edge: lit in the sanctuary, dead and mossy in the monastery */
function toro(lit: boolean): Sprite {
  return memo(`toro${lit}`, () => {
    const [cv, g] = canvas(30, 58), ax = 15, ay = 54;
    const st = lit ? SK.stone : SK.stoneLo, hi = lit ? SK.stoneHi : SK.stone;
    shadow(g, 6, 50, 22, 6, 0.4);
    px(g, 5, 46, 20, 8, K.ink); px(g, 6, 47, 18, 6, st);
    px(g, 11, 26, 8, 21, K.ink); px(g, 12, 26, 6, 21, st); px(g, 12, 26, 2, 21, hi);
    px(g, 7, 22, 16, 5, K.ink); px(g, 8, 23, 14, 3, st);
    px(g, 7, 12, 16, 11, K.ink); px(g, 8, 13, 14, 9, st); px(g, 11, 14, 8, 7, lit ? K.warmHi : K.ink); if (lit) px(g, 13, 15, 4, 5, K.white);
    px(g, 3, 6, 24, 7, K.ink); px(g, 4, 7, 22, 4, hi); px(g, 4, 10, 22, 1, SK.stoneLo); px(g, 13, 1, 4, 6, K.ink); px(g, 14, 2, 2, 4, hi);
    if (!lit) { speckle(g, 3, 6, 24, 48, SK.moss, 0.25, 3); speckle(g, 3, 6, 24, 48, SK.mossHi, 0.06, 4); }
    return { cv, ax, ay };
  });
}
/** a vermilion torii spanning the road: posts `span` hd px apart */
function torii(span: number, broken: boolean): Sprite {
  return memo(`torii${span}${broken}`, () => {
    const W = span + 40, H = 132, [cv, g] = canvas(W, H), ax = W / 2, ay = 128;
    const c = broken ? mix(SK.vermLo, SK.ash, 0.5) : SK.verm, hi = broken ? SK.ash : SK.vermHi, lo = broken ? SK.ashLo : SK.vermLo;
    const p1 = 20 - 5, p2 = 20 + span - 5;
    for (const x of broken ? [p1] : [p1, p2]) {
      shadow(g, x + 2, 124, 14, 5, 0.4);
      px(g, x - 1, 22, 12, 104, K.ink); px(g, x, 22, 10, 104, c); px(g, x, 22, 3, 104, hi); px(g, x + 7, 22, 3, 104, lo);
      px(g, x - 2, 116, 14, 10, K.ink); px(g, x - 1, 117, 12, 8, K.ink); // black-lacquered foot
      if (broken) speckle(g, x, 60, 10, 66, SK.moss, 0.3, 2);
    }
    if (broken) { // the second post is a stump; the lintel has dropped to lean on it
      const x = p2; px(g, x - 1, 96, 12, 30, K.ink); px(g, x, 97, 10, 29, c); px(g, x, 96, 10, 3, SK.ashLo);
      for (let k = 0; k < span; k++) { const yy = 30 + Math.round(k * 64 / span); px(g, p1 + 4 + k, yy - 1, 3, 9, K.ink); px(g, p1 + 4 + k, yy, 3, 6, c); }
      px(g, p1 - 8, 12, 30, 9, K.ink); px(g, p1 - 7, 13, 28, 6, K.ink);
      return { cv, ax, ay };
    }
    px(g, 6, 40, W - 12, 8, K.ink); px(g, 7, 41, W - 14, 6, c); px(g, 7, 41, W - 14, 2, hi); // nuki
    px(g, 0, 12, W, 10, K.ink); px(g, 1, 13, W - 2, 7, K.ink); px(g, 2, 20, W - 4, 6, K.ink); px(g, 3, 21, W - 6, 4, c); px(g, 3, 21, W - 6, 1, hi); // kasagi (black cap) over shimaki
    px(g, 0, 8, 6, 6, K.ink); px(g, W - 6, 8, 6, 6, K.ink); // upturned ends
    px(g, W / 2 - 10, 24, 20, 18, K.ink); px(g, W / 2 - 8, 26, 16, 14, K.ink); frame(g, W / 2 - 8, 26, 16, 14, SK.gold); px(g, W / 2 - 2, 29, 4, 8, SK.goldHi); // gakuzuka plaque
    return { cv, ax, ay };
  });
}
/** the ridge gate: a two-storey sanmon over the ridge road, its Kanemori scanner arch inside */
function ridgeGate(): Sprite {
  return memo('ridgeGate', () => {
    const W = 288, H = 210, [cv, g] = canvas(W, H), ax = W / 2, ay = 206;
    shadow(g, 30, 196, 240, 10, 0.4);
    // posts and the lower wall panels either side of the passage
    for (const x of [30, 238]) { px(g, x - 2, 90, 24, 116, K.ink); px(g, x, 90, 20, 116, SK.verm); px(g, x, 90, 5, 116, SK.vermHi); px(g, x + 15, 90, 5, 116, SK.vermLo); }
    for (const x of [6, 262]) { px(g, x - 2, 110, 24, 96, K.ink); px(g, x, 112, 20, 94, SK.paper); px(g, x, 112, 20, 3, K.wood); }
    // the passage: Kanemori scanner arch inside the gate (cyan bars)
    px(g, 60, 110, 4, 96, K.ink); px(g, 224, 110, 4, 96, K.ink); px(g, 60, 108, 168, 5, K.ink); px(g, 61, 109, 166, 2, K.cyan);
    for (let j = 120; j < 200; j += 12) { px(g, 61, j, 2, 5, K.cyan); px(g, 225, j, 2, 5, K.cyan); }
    // upper storey: balcony rail, lattice windows lit, then the big hip roof seen from the front
    px(g, 20, 62, 248, 30, K.ink); px(g, 22, 64, 244, 26, dk(K.wood, 0.3));
    for (let i = 30; i < 258; i += 28) { px(g, i, 66, 20, 20, K.ink); px(g, i + 2, 68, 16, 16, mix(SK.paper, K.warmHi, 0.6)); px(g, i + 9, 68, 2, 16, K.wood); px(g, i + 2, 75, 16, 2, K.wood); }
    px(g, 14, 88, 260, 6, K.ink); px(g, 16, 89, 256, 3, K.woodHi); for (let i = 18; i < 272; i += 8) px(g, i, 92, 2, 8, K.wood);
    for (let j = 0; j < 50; j++) { const ww = 140 - Math.round(j * 0.25) + Math.round(Math.max(0, 50 - j) * 0.6), y = 12 + j; px(g, W / 2 - ww - 1, y, ww * 2 + 2, 1, K.ink); px(g, W / 2 - ww, y, ww * 2, 1, j < 6 ? dk(K.slate, 0.2) : j % 5 ? dk(K.slate, 0.42) : dk(K.slate, 0.6)); }
    for (let i = 4; i < W - 4; i += 6) px(g, i, 58, 4, 4, dk(K.slate, 0.25));
    px(g, 60, 8, 168, 8, K.ink); px(g, 62, 9, 164, 4, dk(K.slate, 0.15)); for (const x of [54, 222]) { px(g, x, 2, 12, 14, K.ink); px(g, x + 1, 3, 10, 11, SK.gold); }
    px(g, 0, 50, 10, 6, K.ink); px(g, W - 10, 50, 10, 6, K.ink); // flared eaves
    px(g, W / 2 - 22, 30, 44, 20, K.ink); frame(g, W / 2 - 20, 32, 40, 16, SK.gold); px(g, W / 2 - 14, 37, 28, 2, SK.goldHi); px(g, W / 2 - 14, 42, 28, 2, SK.goldHi);
    return { cv, ax, ay };
  });
}
/** a tiled slope seen from the front: rows widen from `w0` to `w1` (`bow` > 1 holds the width back, then flares), tile
 *  courses every 5 rows, a lit left hip and a shaded right hip, eave tile ends on the last row */
function tiledSlope(g: G, cx: number, y0: number, h: number, w0: number, w1: number, bow: number) {
  const T = NEW_TILE;
  for (let j = 0; j < h; j++) {
    const half = Math.round((w0 + (w1 - w0) * (j / (h - 1)) ** bow) / 2), x = cx - half, w = half * 2, y = y0 + j;
    px(g, x - 1, y, w + 2, 1, K.ink); px(g, x, y, w, 1, j % 5 === 0 ? T.line : j % 5 === 1 ? lt(T.s, 0.1) : T.s);
    px(g, x, y, 5, 1, T.w); px(g, x + w - 6, y, 6, 1, T.e);
    for (let i = x + 9; i < x + w - 7; i += 7) px(g, i, y, 1, 1, T.line);
  }
  px(g, cx - w0 / 2 - 1, y0 - 1, w0 + 2, 1, K.ink);
  for (let i = cx - w1 / 2 + 3; i < cx + w1 / 2 - 6; i += 7) { px(g, i, y0 + h - 4, 5, 3, T.ridge); px(g, i + 1, y0 + h - 4, 3, 1, T.ridgeHi); }
}
/** the Kanemori Sanctuary gatehouse: stands on the lodge at the road opening (y84-86) and rises north over the wall - a
 *  tiled skirt roof, a lit upper storey and a tall hip roof with gilded ridge ends and the corp's sensor mast. Its foot
 *  is the lodge's front (the ground art), so the passage itself stays clear; crew behind it see through (Renderer.uprights). */
function gateHall(): Sprite {
  return memo('gateHall', () => {
    const W = 184, H = 188, [cv, g] = canvas(W, H), cx = W / 2, ax = cx, ay = H - 2;
    // skirt roof over the lodge's front
    tiledSlope(g, cx, 164, 22, 108, 138, 1.2);
    px(g, cx - 69, 183, 138, 2, alpha(K.night, 0.5));
    // upper storey: lime wall, timber frame, a warm lattice window
    px(g, cx - 34, 120, 68, 46, K.ink); px(g, cx - 32, 122, 64, 42, SK.paper);
    px(g, cx - 32, 122, 64, 6, alpha(K.night, 0.45));
    for (const x of [cx - 32, cx + 24, cx - 4]) { px(g, x - 1, 122, 10, 42, K.ink); px(g, x, 122, 8, 42, K.woodLo); px(g, x, 122, 2, 42, K.wood); }
    px(g, cx - 18, 134, 36, 24, K.ink); px(g, cx - 16, 136, 32, 20, mix(SK.paper, K.warmHi, 0.65));
    for (let i = cx - 16; i < cx + 16; i += 6) px(g, i, 136, 1, 20, K.woodLo);
    for (let j = 136; j < 156; j += 6) px(g, cx - 16, j, 32, 1, K.woodLo);
    px(g, cx - 34, 160, 68, 4, K.ink); px(g, cx - 33, 161, 66, 2, K.wood); // sill rail
    // the great hip roof, bowed so its lower edge flares
    tiledSlope(g, cx, 26, 96, 44, 176, 2.1);
    px(g, cx - 14, 124, 28, 3, alpha(K.night, 0.5));
    // ridge, gilded ends (shachi) and the corp's sensor mast
    px(g, cx - 30, 16, 60, 10, K.ink); px(g, cx - 29, 18, 58, 6, NEW_TILE.ridge); px(g, cx - 29, 18, 58, 1, NEW_TILE.ridgeHi);
    for (const x of [cx - 40, cx + 28]) { px(g, x, 4, 14, 24, K.ink); px(g, x + 2, 6, 10, 20, SK.gold); px(g, x + 2, 6, 10, 3, SK.goldHi); px(g, x + 4, 12, 6, 2, K.ink); }
    px(g, cx - 1, 0, 2, 16, K.ink); px(g, cx - 1, 0, 2, 3, K.cyan);
    // gilded crest on the roof's face
    px(g, cx - 12, 66, 24, 20, K.ink); frame(g, cx - 10, 68, 20, 16, SK.gold); px(g, cx - 3, 72, 6, 8, SK.goldHi);
    return { cv, ax, ay };
  });
}
/** the bronze finial (sorin) standing on the restored pagoda: nine rings, a water-flame and a jewel */
function sorin(): Sprite {
  return memo('sorin', () => {
    const [cv, g] = canvas(30, 120), ax = 15, ay = 116;
    px(g, 8, 104, 14, 12, K.ink); px(g, 9, 105, 12, 10, SK.bronze);
    px(g, 13, 24, 4, 82, K.ink); px(g, 14, 24, 2, 82, SK.bronzeHi);
    for (let k = 0; k < 9; k++) { const y = 40 + k * 7; px(g, 6, y, 18, 4, K.ink); px(g, 7, y + 1, 16, 2, k % 2 ? SK.gold : SK.goldLo); }
    for (const [x, y] of [[8, 20], [20, 20], [6, 28], [22, 28]]) { px(g, x, y, 3, 8, K.ink); px(g, x + 1, y + 1, 1, 6, SK.goldHi); }
    disc(g, 15, 10, 6, 7, K.ink); disc(g, 15, 10, 4, 5, SK.goldHi); px(g, 14, 1, 2, 4, K.ink);
    return { cv, ax, ay };
  });
}
/** an old grave (haka): a tall carved stone on a two-step base, gone green */
function grave(v: number): Sprite {
  return memo(`grave${v}`, () => {
    const [cv, g] = canvas(26, 50), ax = 13, ay = 46;
    shadow(g, 5, 42, 20, 6, 0.4);
    px(g, 2, 38, 22, 9, K.ink); px(g, 3, 39, 20, 7, SK.stoneLo); px(g, 5, 32, 16, 7, K.ink); px(g, 6, 33, 14, 5, SK.stone);
    const tilt = v > 0.5 ? 2 : 0;
    px(g, 7 + tilt, 6, 12, 27, K.ink); px(g, 8 + tilt, 7, 10, 25, SK.stone); px(g, 8 + tilt, 7, 3, 25, SK.stoneHi);
    for (let j = 10; j < 28; j += 4) px(g, 12 + tilt, j, 3, 2, SK.stoneDk); // carved name
    speckle(g, 2, 6, 22, 40, SK.moss, 0.22, Math.floor(v * 10)); speckle(g, 2, 6, 22, 40, SK.mossHi, 0.05, 3);
    if (v < 0.4) { px(g, 4, 30, 2, 6, K.ink); px(g, 4, 30, 2, 2, K.warmHi); } // a candle stub
    return { cv, ax, ay };
  });
}
/** a small stone jizo with a faded red bib */
function jizo(): Sprite {
  return memo('jizo', () => {
    const [cv, g] = canvas(18, 30), ax = 9, ay = 27;
    shadow(g, 3, 24, 14, 4, 0.4);
    px(g, 4, 10, 10, 16, K.ink); px(g, 5, 11, 8, 14, SK.stone); px(g, 5, 11, 2, 14, SK.stoneHi);
    disc(g, 9, 7, 4, 4, K.ink); disc(g, 9, 7, 3, 3, SK.stone);
    px(g, 4, 12, 10, 6, K.ink); px(g, 5, 13, 8, 4, SK.vermLo);
    speckle(g, 4, 3, 10, 24, SK.moss, 0.25, 7);
    return { cv, ax, ay };
  });
}
/** a checkpoint floodlight mast with a Kanemori camera arm */
function mast(): Sprite {
  return memo('mast', () => {
    const [cv, g] = canvas(40, 120), ax = 20, ay = 116;
    shadow(g, 14, 112, 18, 5, 0.4);
    px(g, 17, 14, 6, 104, K.ink); px(g, 18, 14, 4, 104, K.metal); px(g, 18, 14, 1, 104, K.metalHi);
    px(g, 12, 108, 16, 8, K.ink); px(g, 13, 109, 14, 6, K.metalLo);
    px(g, 4, 6, 32, 12, K.ink); px(g, 5, 7, 30, 10, K.metalLo); for (let i = 7; i < 33; i += 7) { px(g, i, 9, 5, 6, K.white); px(g, i, 9, 5, 1, lt(K.cyan, 0.5)); }
    px(g, 22, 30, 10, 3, K.ink); px(g, 30, 28, 8, 7, K.ink); px(g, 31, 29, 6, 5, K.metal); px(g, 36, 31, 2, 2, K.red);
    px(g, 17, 60, 6, 3, K.cyan);
    return { cv, ax, ay };
  });
}

// ================================================================== placing props and lights
export function sanctuaryProps(m: GameMap, S: HdScene, R: Rect, prop: Prop, lights: HdLight[]) {
  if (R.x + R.w <= SANCT_X) return;
  const t = (x: number, y: number) => tileAt(m, x, y);
  const put = (s: Sprite, wx: number, wy: number) => prop(s.cv, wx, wy, s.ax, s.ay);
  const glow = (wx: number, wy: number, w: number, h: number, color: string, a: number, pool = false) => lights.push({ x: wx - w / 2, y: wy - h / 2, w, h, color, alpha: a, pool });
  const fill = new Set<number>();
  for (const b of S.buildings) if (b.kind === 'sanctFill' && !BOUNDARY_ROOFS.has(b.roof)) for (let y = b.y; y < b.y + b.h; y++) for (let x = b.x; x < b.x + b.w; x++) fill.add(y * m.w + x);
  const solid = (x: number, y: number) => !walk(t(x, y));
  const inR = (tx: number, ty: number) => inTileRect(tx, ty, R) && inSanctuary(tx);

  // cedars: on the forest's front row above a path, where the crown only rises over more forest
  for (let ty = R.y; ty < R.y + R.h; ty++) for (let tx = Math.max(R.x, SANCT_X); tx < R.x + R.w; tx++) {
    if (!fill.has(ty * m.w + tx) || !walk(t(tx, ty + 1))) continue;
    let clear = true; for (let dy = 0; dy <= 4 && clear; dy++) for (let dx = -1; dx <= 1; dx++) if (!solid(tx + dx, ty - dy)) { clear = false; break; }
    if (!clear || hash2(tx * 7, ty * 3) < 0.35) continue;
    const v = Math.floor(hash2(tx, ty) * 3);
    put(cedar(v, isMona(ty)), tx * TILE + 4 + Math.floor(hash2(ty, tx) * 8), ty * TILE + 6);
  }
  // stone lanterns along the mountain road and the ridge road (on the slope side, never over the road)
  for (const a of WORLD.alleys) if (ROAD_NAMES.has(a.name)) {
    const vert = a.h > a.w;
    const sides = vert ? [[a.x - 1, 0], [a.x + a.w, 0]] : [[0, a.y - 1]];
    for (let s = 0; s < (vert ? a.h : a.w); s += 4) for (const [sx, sy] of sides) {
      const tx = vert ? sx : a.x + s, ty = vert ? a.y + s : sy;
      if (!inR(tx, ty) || !solid(tx, ty) || !solid(tx, ty - 1) || !walk(t(vert ? (sx < a.x ? tx + 1 : tx - 1) : tx, vert ? ty : ty + 1))) continue;
      const lit = !isMona(ty), wx = tx * TILE + 8, wy = ty * TILE + 12;
      put(toro(lit), wx, wy);
      if (lit) { glow(wx, wy - 18, 10, 8, K.amber, 0.55); glow(wx + (vert ? (sx < a.x ? 14 : -14) : 0), wy + (vert ? 0 : 12), 46, 26, K.amber, 0.45, true); }
    }
  }
  // stone lanterns of the courtyards (the 1x1 lows)
  for (const l of WORLD.lows) if (l.w === 1 && l.h === 1 && inR(l.x, l.y) && !isMona(l.y)) {
    const wx = l.x * TILE + 8, wy = l.y * TILE + 14; put(toro(true), wx, wy);
    glow(wx, wy - 18, 10, 8, K.amber, 0.6); glow(wx, wy + 6, 50, 30, K.amber, 0.45, true);
  }
  // torii over the road's straight climbs: the first at the checkpoint, one on each switchback, two up the ridge road
  for (const [x0, x1, ty] of [[321, 325, 54], [321, 325, 50]] as const) { // (the countryside road's torii: hd-sanctuary-country.ts)
    if (!inR(x0, ty)) continue;
    const span = ((x1 - x0) * TILE - 4) * 2;
    put(torii(span, false), (x0 + x1 + 1) / 2 * TILE, ty * TILE + 14);
  }
  // the ridge gate over the ridge road where it crosses the ridge wall
  if (inR(322, 45)) {
    put(ridgeGate(), 323.5 * TILE, 45 * TILE + 15);
    glow(323.5 * TILE - 50, 45 * TILE - 46, 14, 10, K.amber, 0.6); glow(323.5 * TILE + 50, 45 * TILE - 46, 14, 10, K.amber, 0.6);
    glow(323.5 * TILE, 46.5 * TILE, 60, 30, K.cyan, 0.4, true); glow(323.5 * TILE, 48.5 * TILE, 70, 34, K.amber, 0.4, true);
  }
  // the restored pagoda's finial
  const pg = S.buildings.find(b => b.id === 'pagoda');
  if (pg) { // centre of the pyramid roof, as hd-sanctuary.ts sanctPagodaRoof lays it out
    const roofH = (pg.h - pg.face) * HT, s = Math.min(pg.w * HT - 8, roofH - 8);
    put(sorin(), (pg.x + pg.w / 2) * TILE, pg.y * TILE + (roofH - s / 2 - 4) / 2);
  }
  // checkpoint: floodlight masts on the slope beside the two guard pads
  for (const [tx, ty] of [[263, 80], [263, 88]] as const) if (inR(tx, ty)) {
    put(mast(), tx * TILE + 10, ty * TILE + 12);
    glow(tx * TILE + 10, ty * TILE - 44, 30, 10, K.white, 0.7); glow(tx * TILE + 50, ty * TILE + 24, 110, 56, K.white, 0.42, true);
  }
  // the gatehouse over the lodge at the road opening, and stone lanterns (toro) along the inside of the boundary wall
  if (inR(256, 81)) { put(gateHall(), 257 * TILE, 82 * TILE); glow(257 * TILE, 82 * TILE - 6, 70, 14, K.amber, 0.4, true); }
  for (let ty = 46; ty < R.y + R.h && ty < 144; ty += 8) {
    if (ty >= 76 && ty < 92 || ty < R.y || !inR(258, ty) || solidFoot(t(258, ty))) continue;
    const wx = 258 * TILE + 3, wy = ty * TILE + 13;
    put(toro(true), wx, wy); glow(wx, wy - 18, 10, 8, K.amber, 0.5); glow(wx + 10, wy + 4, 44, 24, K.amber, 0.38, true);
  }
  if (inR(266, 81)) glow(270 * TILE, 85.5 * TILE, 90, 40, K.cyan, 0.35, true); // the gate lane under the booth's light
  // hall lantern pools on the gravel, bell tower and kura
  if (inR(300, 66)) { glow(306 * TILE, 67 * TILE, 220, 40, K.amber, 0.45, true); glow(318 * TILE, 66.5 * TILE, 40, 24, K.amber, 0.35, true); }
  // monastery: graves, jizo, a fallen torii at the top of the ridge road, candles at a few graves
  for (const l of WORLD.lows) if (l.w === 1 && l.h === 1 && inR(l.x, l.y) && isMona(l.y)) {
    const v = hash2(l.x, l.y); put(grave(Math.round(v * 4) / 4), l.x * TILE + 8, l.y * TILE + 13);
    if (v < 0.4) glow(l.x * TILE + 3, l.y * TILE + 9, 12, 10, K.amber, 0.32);
  }
  for (let ty = 13; ty < 27; ty += 2) if (inR(281, ty) && solid(281, ty) && walk(t(282, ty))) put(jizo(), 281 * TILE + 9, ty * TILE + 10);
  if (inR(321, 42)) put(torii(((325 - 321) * TILE - 4) * 2, true), 323.5 * TILE, 42 * TILE + 14);
  for (const r of WORLD.thins) if (inR(r.x, r.y) && isMona(r.y)) put(toro(false), r.x * TILE + 8, r.y * TILE + 12);
  glow(311 * TILE, 28.5 * TILE, 40, 20, K.amber, 0.3, true); // the one candle in the hall, on the step
  countryProps(m, R, prop, lights, v => cedar(v, false), toro, torii);
}
